import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Book, BookType } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { getGraphQLClient, TOGGLE_FAVORITE } from '../../lib/graphql';

interface BookCardProps {
  book: Book;
  onDelete?: (id: string) => void;
  onPublish?: (id: string, published: boolean) => void;
  showPublishButton?: boolean;
  showFavoriteButton?: boolean;
  showStats?: boolean;
  variant?: 'default' | 'gallery';
  onFavoriteToggle?: (bookId: string, isFavorited: boolean) => void;
}

export default function BookCard({
  book,
  onDelete,
  onPublish,
  showPublishButton = false,
  showFavoriteButton = true,
  showStats = false,
  variant = 'default',
  onFavoriteToggle,
}: BookCardProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(book.isFavorited);
  const [favoritesCount, setFavoritesCount] = useState(book.favoritesCount || 0);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        toggleFavorite: {
          book: { id: string; favoritesCount: number; isFavorited: boolean };
          favorited: boolean;
          errors: string[];
        };
      }>(TOGGLE_FAVORITE, { bookId: book.id });

      if (data.toggleFavorite.errors.length === 0) {
        setIsFavorited(data.toggleFavorite.favorited);
        setFavoritesCount(data.toggleFavorite.book.favoritesCount);
        onFavoriteToggle?.(book.id, data.toggleFavorite.favorited);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const bookTypeColors: { [key in BookType]: string } = {
    fiction: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    non_fiction: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };

  const themeColors: { [key: string]: string } = {
    fantasy: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    sci_fi: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    romance: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    mystery: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    thriller: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    horror: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    adventure: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    drama: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    comedy: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    historical: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    dystopian: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
    urban_fantasy: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    biography: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    history: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    science: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
    self_help: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400',
    travel: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    cooking: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    business: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
    technology: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    philosophy: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    psychology: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    love: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    nature: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    dark: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    philosophical: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    humorous: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    spiritual: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    epic: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    romantic: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    modernist: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
  };

  const statusColors: { [key: string]: string } = {
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    in_progress: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };

  const isGallery = variant === 'gallery';

  // Determine the link path based on book status
  let linkPath: string;
  if (book.published) {
    linkPath = `/books/${book.id}`;
  } else {
    linkPath = `/books/${book.id}`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className={`card-hover flex flex-col relative overflow-hidden group ${isGallery ? 'p-0' : 'p-6'}`}
    >
      {/* Cover image or gradient placeholder - shown for both variants */}
      {book.coverImageUrl && (
        <div className={`relative overflow-hidden ${isGallery ? 'h-48' : 'h-32 -mx-6 -mt-6 mb-4'}`}>
          {/* Note: Default variant uses negative margins to extend cover to card edges,
              depends on parent card having p-6 padding */}
          <img
            src={book.coverImageUrl}
            alt={book.fullTitle || book.title || 'Book cover'}
            className="w-full h-full object-cover"
          />
          {isGallery && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-800 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex gap-2 flex-wrap">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${bookTypeColors[book.bookType?.toLowerCase() as BookType] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
                  >
                    {t(`bookTypes.${book.bookType?.toLowerCase()}`)}
                  </span>
                  {book.themes?.slice(0, 1).map((theme) => (
                    <span
                      key={theme}
                      className={`px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${themeColors[theme] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
                    >
                      {t(`themes.${theme}`, theme)}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
          {/* Favorite button overlay */}
          {showFavoriteButton && book.published && isGallery && (
            <button
              onClick={handleToggleFavorite}
              disabled={!user || isTogglingFavorite}
              className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
                isFavorited
                  ? 'bg-red-500/90 text-white shadow-lg'
                  : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-red-500/90 hover:text-white'
              } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={
                user
                  ? isFavorited
                    ? 'Remove from favorites'
                    : 'Add to favorites'
                  : 'Login to favorite'
              }
            >
              <svg
                className="w-5 h-5"
                fill={isFavorited ? 'currentColor' : 'none'}
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      <div className={isGallery ? 'p-4 flex-1 flex flex-col' : 'flex-1 flex flex-col'}>
        {/* Default variant: badges */}
        {!isGallery && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex gap-2 flex-wrap">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${bookTypeColors[book.bookType?.toLowerCase() as BookType] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
              >
                {t(`bookTypes.${book.bookType?.toLowerCase()}`)}
              </span>
              {book.themes?.slice(0, 2).map((theme) => (
                <span
                  key={theme}
                  className={`px-2 py-1 rounded-full text-xs font-medium ${themeColors[theme] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
                >
                  {t(`themes.${theme}`, theme)}
                </span>
              ))}
              {book.themes && book.themes.length > 2 && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                  +{book.themes.length - 2}
                </span>
              )}
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[book.status.toLowerCase()]}`}
              >
                {t(`statuses.${book.status.toLowerCase()}`)}
              </span>
              {book.published && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  <svg
                    className="w-3 h-3 inline-block mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t('books.published')}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              {showFavoriteButton && book.published && (
                <button
                  onClick={handleToggleFavorite}
                  disabled={!user || isTogglingFavorite}
                  className={`p-1 transition-all duration-200 ${
                    isFavorited
                      ? 'text-red-500 hover:text-red-600'
                      : 'text-gray-400 hover:text-red-500'
                  } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={
                    user
                      ? isFavorited
                        ? 'Remove from favorites'
                        : 'Add to favorites'
                      : 'Login to favorite'
                  }
                >
                  <svg
                    className="w-5 h-5"
                    fill={isFavorited ? 'currentColor' : 'none'}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </button>
              )}
              {showPublishButton && onPublish && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onPublish(book.id, !book.published);
                  }}
                  className={`p-1 transition-colors ${
                    book.published
                      ? 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
                      : 'text-gray-400 hover:text-green-500 dark:hover:text-green-400'
                  }`}
                  title={book.published ? t('books.unpublish') : t('books.publish')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onDelete(book.id);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        <Link to={linkPath} className="flex-1">
          <h3
            className={`font-semibold text-gray-900 dark:text-gray-100 mb-2 hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-2 ${isGallery ? 'text-base' : 'text-lg'}`}
          >
            {book.fullTitle || book.title || 'Untitled'}
          </h3>

          {book.user && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">by {book.user.name}</p>
          )}

          {book.summary && (
            <p
              className={`text-sm text-gray-600 dark:text-gray-400 mb-4 ${isGallery ? 'line-clamp-2' : 'line-clamp-2'}`}
            >
              {book.summary}
            </p>
          )}
        </Link>

        <div
          className={`flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-auto pt-4 border-t border-gray-100 dark:border-gray-700 ${isGallery ? 'flex-wrap gap-3' : ''}`}
        >
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {book.plotBeatsCount} {t('books.beats')}
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
            {book.wordCount.toLocaleString()} {t('books.words')}
          </span>
          {showStats && (
            <>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                {(book.viewsCount || 0).toLocaleString()}
              </span>
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill={isFavorited ? 'currentColor' : 'none'}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {favoritesCount.toLocaleString()}
              </span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
